import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Tags, Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { LABEL_COLORS, labelColor } from "@/lib/labelColors";

const COLOR_KEYS = Object.keys(LABEL_COLORS);

export default function LabelManager({ labels, onChange, isAdmin, trigger }) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: "", color: "sky" });

  const save = async () => {
    const name = draft.name.trim();
    if (!name) { toast.error("Name erforderlich"); return; }
    try {
      if (editingId) {
        await api.patch(`/labels/${editingId}`, { name, color: draft.color });
      } else {
        await api.post("/labels", { name, color: draft.color });
      }
      setEditingId(null);
      setDraft({ name: "", color: "sky" });
      toast.success(editingId ? "Label aktualisiert" : "Label erstellt");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const startEdit = (l) => { setEditingId(l.id); setDraft({ name: l.name, color: l.color }); };
  const cancelEdit = () => { setEditingId(null); setDraft({ name: "", color: "sky" }); };

  const del = async (id, name) => {
    if (!window.confirm(`Label „${name}" wirklich löschen? Es wird von allen Karten entfernt.`)) return;
    try {
      await api.delete(`/labels/${id}`);
      toast.success("Label gelöscht");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="h-10" data-testid="btn-manage-labels">
            <Tags className="h-4 w-4 mr-2" /> Labels
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="label-manager">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Tags className="h-5 w-5 text-kauz-navy" /> Labels verwalten
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {labels.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-6">Noch keine Labels angelegt.</p>
          )}
          {labels.map((l) => {
            const c = labelColor(l.color);
            const isEditing = editingId === l.id;
            return (
              <div key={l.id} data-testid={`label-row-${l.id}`} className="flex items-center gap-2 group">
                {isEditing ? (
                  <>
                    <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      className="h-8 flex-1" data-testid="label-edit-name" autoFocus />
                    <ColorPicker color={draft.color} onChange={(v) => setDraft({ ...draft, color: v })} />
                    <button onClick={save} className="h-8 w-8 grid place-items-center rounded text-emerald-700 hover:bg-emerald-50" data-testid="label-edit-save">
                      <Check className="h-4 w-4" />
                    </button>
                    <button onClick={cancelEdit} className="h-8 w-8 grid place-items-center rounded text-slate-500 hover:bg-slate-100">
                      <X className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className={`flex-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border ${c.bg} ${c.border} ${c.text} text-sm font-medium`}>
                      <span className={`h-2 w-2 rounded-full ${c.dot}`} />
                      {l.name}
                    </div>
                    {isAdmin && (
                      <>
                        <button onClick={() => startEdit(l)} className="h-8 w-8 grid place-items-center rounded text-slate-500 hover:bg-slate-100 opacity-0 group-hover:opacity-100" data-testid={`label-edit-${l.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => del(l.id, l.name)} className="h-8 w-8 grid place-items-center rounded text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100" data-testid={`label-delete-${l.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {isAdmin && !editingId && (
          <div className="border-t pt-3 mt-2 space-y-2">
            <div className="ph-label">Neues Label</div>
            <div className="flex items-center gap-2">
              <Input placeholder="z.B. Kunde" value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="h-9 flex-1" data-testid="label-new-name" />
              <ColorPicker color={draft.color} onChange={(v) => setDraft({ ...draft, color: v })} />
              <Button onClick={save} size="sm" className="bg-sky-700 hover:bg-sky-800 h-9" data-testid="label-new-submit">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ColorPicker({ color, onChange }) {
  const [open, setOpen] = useState(false);
  const c = labelColor(color);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className={`h-9 w-9 rounded-md border ${c.bg} ${c.border} grid place-items-center`}
        data-testid="label-color-toggle" title="Farbe">
        <span className={`h-3 w-3 rounded-full ${c.dot}`} />
      </button>
      {open && (
        <div className="absolute z-50 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-2 grid grid-cols-5 gap-1 w-44">
          {COLOR_KEYS.map((k) => {
            const cc = labelColor(k);
            return (
              <button key={k} type="button"
                data-testid={`label-color-${k}`}
                onClick={() => { onChange(k); setOpen(false); }}
                className={`h-7 w-7 rounded-md border ${cc.bg} ${cc.border} grid place-items-center ${k === color ? "ring-2 ring-offset-1 ring-sky-600" : ""}`}
                title={cc.label}>
                <span className={`h-2 w-2 rounded-full ${cc.dot}`} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
